import React from 'react';
import {
  FileText,
  List,
  Settings,
  BookOpen,
} from 'lucide-react';
import NavItem from './NavItem';
import UserInfoBox from './UserInfoBox';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Sidebar: React.FC = () => {
  const { tenant, availableTenants, switchTenant } = useAuth();

  const handleTenantChange = (tenantId: string) => {
    if (tenantId !== tenant?.id) {
      switchTenant(tenantId);
    }
  };

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-card border-r border-border flex flex-col">
      {/* Logo Section */}
      <div className="h-16 flex items-center px-4 border-b border-border">
        <div className="flex items-center gap-3">
          <img src="/finrite-icon.svg" alt="Finrite" className="flex-shrink-0 w-8 h-8" />
          <span className="font-bold text-xl text-foreground">Finrite</span>
        </div>
      </div>

      {/* Navigation Section */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Setup Guide */}
        <NavItem
          icon={<BookOpen className="h-5 w-5" />}
          label="Setup Guide"
          to="/onboarding"
        />

        {/* Deals */}
        <NavItem
          icon={<FileText className="h-5 w-5" />}
          label="Deals"
          to="/"
        />

        {/* Manual Mappings */}
        <NavItem
          icon={<List className="h-5 w-5" />}
          label="Mappings"
          to="/product_mappings"
        />

        {/* Settings */}
        <NavItem
          icon={<Settings className="h-5 w-5" />}
          label="Settings"
          to="/settings"
        />

      </nav>

      {/* Tenant Selector */}
      <div className="px-3 py-3 border-t border-border">
        <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">
          Environment
        </Label>
        <Select
          value={tenant?.id ?? ''}
          onValueChange={handleTenantChange}
        >
          <SelectTrigger className="w-full h-9">
            <SelectValue placeholder="Select tenant" />
          </SelectTrigger>
          <SelectContent>
            {availableTenants.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                <span className="flex items-center gap-2">
                  {t.name ?? t.id}
                  <Badge
                    variant="secondary"
                    className={
                      t.is_test
                        ? 'bg-amber-100 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0'
                        : 'bg-green-100 text-green-800 border-green-200 text-[10px] px-1.5 py-0'
                    }
                  >
                    {t.is_test ? 'Test' : 'Prod'}
                  </Badge>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* User Info Section */}
      <UserInfoBox />
    </aside>
  );
};

export default Sidebar;
